import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  TextField,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Link,
  LinearProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Checkbox,
  Stack,
  Radio,
  RadioGroup,
  FormControl,
  FormControlLabel,
} from "@mui/material";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PersonIcon from "@mui/icons-material/Person";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ControlPointIcon from "@mui/icons-material/ControlPoint";
// Ensure this path is correct for your project
import NCG_logo from "../../assets/img/ncg_logo.png";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import {
  LikeButton,
  CommentsSection,
  type Comment,
} from "../basicSearchScreen/vdeoPlayer";

// --- TYPESCRIPT DECLARATIONS FOR YOUTUBE API ---
declare global {
  interface Window {
    YT: any;
    onYouTubeIframeAPIReady: () => void;
  }
}

// --- TYPES ---
interface VideoRecord {
  id: string;
  title: string;
  category: string;
  subCategory: string;
  location: string;
  gender: string;
  ageGroup: string;
  date: string;
  hospital: string;
  isNew: boolean;
  isPopular: boolean;
  isSaved: boolean;
  image: string;
  videoUrl: string;
  description: string;
  likes: number;
  commentsList: Comment[];
  stageOfCancer: string;
}

// --- HELPER TO EXTRACT YOUTUBE ID ---
const extractVideoId = (url: string) => {
  const match = url.match(/[?&]v=([^&]+)/);
  return match ? match[1] : null;
};

const dummyJsonData: VideoRecord[] = [
  {
    id: "1",
    title: "Pediatric Brain Tumors: Past, Present and Future",
    category: "PEDIATRIC",
    subCategory: "Neuro-Oncology",
    location: "Head",
    gender: "All",
    ageGroup: "0-18 Y",
    date: "Apr 2021",
    hospital: "Norton Healthcare",
    isNew: false,
    isPopular: true,
    isSaved: true,
    stageOfCancer: "II",
    image: "https://img.youtube.com/vi/Rb2M8V4PHOk/hqdefault.jpg",
    videoUrl: "https://www.youtube.com/watch?v=Rb2M8V4PHOk",
    description:
      "Pediatric grand rounds provide physicians a continuous update on emerging trends in caring for the pediatric patient.",
    likes: 245,
    commentsList: [
      {
        user_id: 1,
        first_name: "Dr. Ananya",
        vid_comment: "Excellent overview of current treatment protocols.",
        updated_on: "2024-02-10T09:15:00Z",
      },
      {
        user_id: 2,
        first_name: "Rahul M",
        vid_comment:
          "Very helpful for residents preparing for clinical rounds.",
        updated_on: "2024-03-05T14:30:00Z",
      },
      {
        user_id: 3,
        first_name: "Priya S",
        vid_comment:
          "The section on future therapies was particularly insightful.",
        updated_on: "2024-04-01T11:00:00Z",
      },
      {
        user_id: 4,
        first_name: "Dr. Vikram",
        vid_comment:
          "Clear explanation of molecular targets in pediatric gliomas.",
        updated_on: "2024-04-15T08:20:00Z",
      },
      {
        user_id: 5,
        first_name: "Kavitha R",
        vid_comment:
          "Loved the historical context before diving into current protocols.",
        updated_on: "2024-05-02T13:10:00Z",
      },
      {
        user_id: 6,
        first_name: "Suresh P",
        vid_comment: "The survival rate data was sobering yet hopeful.",
        updated_on: "2024-05-20T10:45:00Z",
      },
      {
        user_id: 7,
        first_name: "Dr. Meera",
        vid_comment:
          "Would appreciate a follow-up on immunotherapy approaches.",
        updated_on: "2024-06-01T09:00:00Z",
      },
      {
        user_id: 8,
        first_name: "Anil K",
        vid_comment: "Shared this with my team — very relevant for our rounds.",
        updated_on: "2024-06-18T15:30:00Z",
      },
      {
        user_id: 9,
        first_name: "Lakshmi T",
        vid_comment:
          "The comparison of surgical vs radiation outcomes was very clear.",
        updated_on: "2024-07-03T11:15:00Z",
      },
      {
        user_id: 10,
        first_name: "Dr. Raghavan",
        vid_comment:
          "One of the best pediatric neuro-oncology summaries I have seen.",
        updated_on: "2024-07-20T14:00:00Z",
      },
    ],
  },
  {
    id: "2",
    title: "The Tumor Board and Personalized Cancer Care",
    category: "ONCOLOGY",
    subCategory: "General",
    location: "Systemic",
    gender: "All",
    ageGroup: "All Ages",
    date: "Sep 2024",
    hospital: "Manipal Hospital",
    isNew: true,
    isPopular: true,
    isSaved: false,
    stageOfCancer: "III",
    image: "https://img.youtube.com/vi/73eXGC49OgE/hqdefault.jpg",
    videoUrl: "https://www.youtube.com/watch?v=73eXGC49OgE",
    description:
      "A multidisciplinary approach to cancer care that brings together a team of experts to collaboratively discuss each patient's case.",
    likes: 512,
    commentsList: [
      {
        user_id: 11,
        first_name: "Dr. Mehta",
        vid_comment: "The multidisciplinary approach here is truly exemplary.",
        updated_on: "2024-05-12T08:45:00Z",
      },
      {
        user_id: 12,
        first_name: "Sunita K",
        vid_comment:
          "Really highlights the importance of collaboration in oncology.",
        updated_on: "2024-06-20T16:00:00Z",
      },
      {
        user_id: 13,
        first_name: "Rajan V",
        vid_comment:
          "Tumor boards should be standard practice at every hospital.",
        updated_on: "2024-07-05T10:30:00Z",
      },
      {
        user_id: 14,
        first_name: "Dr. Pooja",
        vid_comment:
          "Great insight into how personalized care decisions are made.",
        updated_on: "2024-07-18T09:15:00Z",
      },
      {
        user_id: 15,
        first_name: "Mohan D",
        vid_comment:
          "The case studies included made the concept very tangible.",
        updated_on: "2024-08-01T14:00:00Z",
      },
      {
        user_id: 16,
        first_name: "Dr. Nair",
        vid_comment:
          "Wish more institutions would adopt this model systematically.",
        updated_on: "2024-08-14T11:45:00Z",
      },
      {
        user_id: 17,
        first_name: "Geeta S",
        vid_comment:
          "Patient outcomes clearly improve with a coordinated team.",
        updated_on: "2024-08-28T13:30:00Z",
      },
      {
        user_id: 18,
        first_name: "Dr. Ashok",
        vid_comment:
          "The oncologist's role in coordinating the board is well explained.",
        updated_on: "2024-09-10T08:00:00Z",
      },
    ],
  },
  {
    id: "3",
    title: "Neuroblastoma: The Children's Oncology Group",
    category: "PEDIATRIC",
    subCategory: "Solid Tumour",
    location: "Adrenal",
    gender: "Male",
    ageGroup: "0-10 Y",
    date: "Sep 2023",
    hospital: "Colorado Children's Hospital",
    isNew: false,
    isPopular: false,
    isSaved: false,
    stageOfCancer: "I",
    image: "https://img.youtube.com/vi/uYu6zHlBJl8/hqdefault.jpg",
    videoUrl: "https://www.youtube.com/watch?v=uYu6zHlBJl8",
    description:
      "Discussion on the Children's Oncology Group and their work in neuroblastoma.",
    likes: 120,
    commentsList: [
      {
        user_id: 19,
        first_name: "Arjun T",
        vid_comment: "Great case discussion on neuroblastoma staging.",
        updated_on: "2023-11-08T10:20:00Z",
      },
      {
        user_id: 20,
        first_name: "Dr. Shalini",
        vid_comment: "MYCN amplification criteria nicely explained.",
        updated_on: "2023-11-22T09:00:00Z",
      },
      {
        user_id: 21,
        first_name: "Prakash N",
        vid_comment: "The risk stratification table was very helpful.",
        updated_on: "2023-12-05T14:30:00Z",
      },
      {
        user_id: 22,
        first_name: "Dr. Iyer",
        vid_comment:
          "Would love a session specifically on high-risk neuroblastoma.",
        updated_on: "2023-12-19T11:00:00Z",
      },
      {
        user_id: 23,
        first_name: "Anita R",
        vid_comment: "Excellent work by the Children's Oncology Group.",
        updated_on: "2024-01-08T08:45:00Z",
      },
    ],
  },
  {
    id: "4",
    title: "Updates in Pediatric Neuro-oncology",
    category: "PEDIATRIC",
    subCategory: "Neuro-oncology",
    location: "Brain",
    gender: "Female",
    ageGroup: "11-20 Y",
    date: "Dec 2022",
    hospital: "Norton Cancer Institute",
    isNew: false,
    isPopular: true,
    isSaved: false,
    stageOfCancer: "II",
    image: "https://img.youtube.com/vi/x-xINDQ98to/hqdefault.jpg",
    videoUrl: "https://www.youtube.com/watch?v=x-xINDQ98to",
    description:
      "Identify state-of-the-art care for cancer patients based on interdisciplinary tumor board consensus.",
    likes: 340,
    commentsList: [
      {
        user_id: 24,
        first_name: "Dr. Lakshmi",
        vid_comment: "State-of-the-art neuro-oncology updates well presented.",
        updated_on: "2023-01-15T09:00:00Z",
      },
      {
        user_id: 25,
        first_name: "Vijay R",
        vid_comment:
          "The pediatric cases are always challenging — well covered.",
        updated_on: "2023-02-28T13:45:00Z",
      },
      {
        user_id: 26,
        first_name: "Dr. Chandra",
        vid_comment: "DIPG management section was particularly eye-opening.",
        updated_on: "2023-03-12T10:30:00Z",
      },
      {
        user_id: 27,
        first_name: "Sneha G",
        vid_comment:
          "Glad to see adolescent patients getting dedicated attention.",
        updated_on: "2023-04-05T08:15:00Z",
      },
      {
        user_id: 28,
        first_name: "Dr. Balaji",
        vid_comment: "Tumor board consensus approach is the right way forward.",
        updated_on: "2023-04-22T14:00:00Z",
      },
      {
        user_id: 29,
        first_name: "Rekha M",
        vid_comment:
          "Shared with my colleagues in the pediatric oncology unit.",
        updated_on: "2023-05-10T11:30:00Z",
      },
      {
        user_id: 30,
        first_name: "Dr. Subramaniam",
        vid_comment: "Great overview of evolving treatment guidelines.",
        updated_on: "2023-05-28T09:45:00Z",
      },
    ],
  },
  {
    id: "5",
    title: "Adult Hematolymphoid - T Cell Lymphoma Case",
    category: "ONCOLOGY",
    subCategory: "Hematolymphoid",
    location: "Systemic",
    gender: "Male",
    ageGroup: "52 Y",
    date: "Sep 2019",
    hospital: "Malabar Cancer Centre",
    isNew: false,
    isPopular: false,
    isSaved: true,
    stageOfCancer: "III",
    image: "https://img.youtube.com/vi/nrn37DXQZCU/hqdefault.jpg",
    videoUrl: "https://www.youtube.com/watch?v=nrn37DXQZCU",
    description:
      "National Cancer Grid tumor board discussion on an Adult Hematolymphoid case featuring a 52-year-old male with primary T Cell Lymphoma.",
    likes: 98,
    commentsList: [
      {
        user_id: 31,
        first_name: "Nisha P",
        vid_comment: "Good walkthrough of the T Cell Lymphoma case.",
        updated_on: "2022-07-19T11:30:00Z",
      },
      {
        user_id: 32,
        first_name: "Dr. Krishnan",
        vid_comment:
          "The treatment decision at the tumor board was well reasoned.",
        updated_on: "2022-08-02T09:00:00Z",
      },
      {
        user_id: 33,
        first_name: "Arun S",
        vid_comment: "Useful for understanding hematolymphoid classification.",
        updated_on: "2022-08-20T14:15:00Z",
      },
      {
        user_id: 34,
        first_name: "Dr. Padma",
        vid_comment: "The imaging correlation shown here was very instructive.",
        updated_on: "2022-09-05T10:00:00Z",
      },
    ],
  },
  {
    id: "6",
    title: "Low Grade B Cell Lymphoma - Case Review",
    category: "ONCOLOGY",
    subCategory: "Hematolymphoid",
    location: "Systemic",
    gender: "Female",
    ageGroup: "55 Y",
    date: "Sep 2019",
    hospital: "JIPMER",
    isNew: false,
    isPopular: true,
    isSaved: false,
    stageOfCancer: "II",
    image: "https://img.youtube.com/vi/cKaEU0O2JQU/hqdefault.jpg",
    videoUrl: "https://www.youtube.com/watch?v=cKaEU0O2JQU",
    description:
      "NCG Tumor Board case review discussing a 55-year-old female patient with Low Grade B Cell Lymphoma.",
    likes: 410,
    commentsList: [
      {
        user_id: 35,
        first_name: "Dr. Kavya",
        vid_comment:
          "The renal involvement angle in this case is very educative.",
        updated_on: "2022-08-05T15:10:00Z",
      },
      {
        user_id: 36,
        first_name: "Arun B",
        vid_comment: "Excellent NCG tumor board discussion.",
        updated_on: "2022-09-12T09:55:00Z",
      },
      {
        user_id: 37,
        first_name: "Meena J",
        vid_comment: "Would love to see more such case reviews.",
        updated_on: "2022-10-01T14:20:00Z",
      },
      {
        user_id: 38,
        first_name: "Dr. Rajiv",
        vid_comment: "The watch-and-wait approach decision was well justified.",
        updated_on: "2022-10-18T10:30:00Z",
      },
      {
        user_id: 39,
        first_name: "Sujatha V",
        vid_comment:
          "Impressed by how the board reached consensus so efficiently.",
        updated_on: "2022-11-02T08:45:00Z",
      },
      {
        user_id: 40,
        first_name: "Dr. Naresh",
        vid_comment: "Flow cytometry interpretation was crystal clear.",
        updated_on: "2022-11-20T13:00:00Z",
      },
      {
        user_id: 41,
        first_name: "Poonam K",
        vid_comment: "This case mirrors one we had — very helpful reference.",
        updated_on: "2022-12-05T11:15:00Z",
      },
      {
        user_id: 42,
        first_name: "Dr. Sundar",
        vid_comment: "The role of PET-CT was nicely highlighted here.",
        updated_on: "2022-12-22T09:30:00Z",
      },
      {
        user_id: 43,
        first_name: "Ramya T",
        vid_comment: "Great example of collaborative oncology at its best.",
        updated_on: "2023-01-08T14:45:00Z",
      },
      {
        user_id: 44,
        first_name: "Dr. Bhatia",
        vid_comment: "Rituximab dosing rationale was clearly explained.",
        updated_on: "2023-01-25T10:00:00Z",
      },
    ],
  },
  {
    id: "7",
    title: "CLL or SLL Needs Biopsy Confirmation",
    category: "ONCOLOGY",
    subCategory: "Hematolymphoid",
    location: "Systemic",
    gender: "Male",
    ageGroup: "60 Y",
    date: "Sep 2019",
    hospital: "IGGMC Nagpur",
    isNew: true,
    isPopular: false,
    isSaved: false,
    stageOfCancer: "I",
    image: "https://img.youtube.com/vi/yiFxruo4Stc/hqdefault.jpg",
    videoUrl: "https://www.youtube.com/watch?v=yiFxruo4Stc",
    description:
      "A 60-year-old male patient case discussion exploring the necessity of biopsy for confirming CLL or SLL diagnosis.",
    likes: 185,
    commentsList: [
      {
        user_id: 45,
        first_name: "Dr. Ramesh",
        vid_comment: "Biopsy confirmation criteria clearly explained.",
        updated_on: "2022-03-22T10:00:00Z",
      },
      {
        user_id: 46,
        first_name: "Deepa N",
        vid_comment: "Very relevant for CLL/SLL differential diagnosis.",
        updated_on: "2022-04-18T16:30:00Z",
      },
      {
        user_id: 47,
        first_name: "Dr. Kapoor",
        vid_comment: "Immunohistochemistry panel selection was well discussed.",
        updated_on: "2022-05-05T09:15:00Z",
      },
      {
        user_id: 48,
        first_name: "Vinod S",
        vid_comment:
          "Clear guidance on when to proceed with bone marrow biopsy.",
        updated_on: "2022-05-22T13:45:00Z",
      },
      {
        user_id: 49,
        first_name: "Dr. Rao",
        vid_comment:
          "This is a common clinical dilemma — glad it was addressed.",
        updated_on: "2022-06-08T10:30:00Z",
      },
      {
        user_id: 50,
        first_name: "Sudha P",
        vid_comment: "The Rai staging overview helped me refresh my knowledge.",
        updated_on: "2022-06-25T14:00:00Z",
      },
    ],
  },
  {
    id: "8",
    title: "Prostate Cancer: Precision Medicine",
    category: "UROLOGY",
    subCategory: "Prostate",
    location: "Pelvis",
    gender: "Male",
    ageGroup: "60+ Y",
    date: "Nov 2023",
    hospital: "Fortis Healthcare",
    isNew: false,
    isPopular: false,
    isSaved: true,
    stageOfCancer: "IV",
    image: "https://img.youtube.com/vi/46Xh7OFkkCE/hqdefault.jpg",
    videoUrl: "https://www.youtube.com/watch?v=46Xh7OFkkCE",
    description:
      "The role of genomic sequencing and precision medicine in aggressive prostate cancer variants.",
    likes: 275,
    commentsList: [
      {
        user_id: 51,
        first_name: "Dr. Sathish",
        vid_comment: "Genomic sequencing in prostate cancer is the future.",
        updated_on: "2023-12-10T08:00:00Z",
      },
      {
        user_id: 52,
        first_name: "Harish V",
        vid_comment: "BRCA2 mutation discussion was very practical.",
        updated_on: "2023-12-28T10:30:00Z",
      },
      {
        user_id: 53,
        first_name: "Dr. Menon",
        vid_comment: "PARP inhibitor section was the highlight for me.",
        updated_on: "2024-01-12T09:15:00Z",
      },
      {
        user_id: 54,
        first_name: "Gopal R",
        vid_comment: "The liquid biopsy concept explained here is excellent.",
        updated_on: "2024-01-28T14:45:00Z",
      },
      {
        user_id: 55,
        first_name: "Dr. Sheela",
        vid_comment: "Very relevant for our uro-oncology team.",
        updated_on: "2024-02-14T11:00:00Z",
      },
      {
        user_id: 56,
        first_name: "Natarajan K",
        vid_comment:
          "Would love a deeper dive into enzalutamide vs abiraterone.",
        updated_on: "2024-03-01T13:30:00Z",
      },
      {
        user_id: 57,
        first_name: "Dr. Pillai",
        vid_comment: "PSA doubling time correlation was very well presented.",
        updated_on: "2024-03-18T09:00:00Z",
      },
      {
        user_id: 58,
        first_name: "Anand S",
        vid_comment: "Great resource for precision oncology education.",
        updated_on: "2024-04-05T15:00:00Z",
      },
    ],
  },
  {
    id: "9",
    title: "What is the National Cancer Grid?",
    category: "GENERAL",
    subCategory: "Overview",
    location: "India",
    gender: "All",
    ageGroup: "All Ages",
    date: "Jul 2020",
    hospital: "NCG Consortium",
    isNew: false,
    isPopular: true,
    isSaved: false,
    stageOfCancer: "I",
    image: "https://img.youtube.com/vi/Rms-2gZyjyk/hqdefault.jpg",
    videoUrl: "https://www.youtube.com/watch?v=Rms-2gZyjyk",
    description:
      "An overview of the National Cancer Grid, a consortium of 200+ cancer centers with the mandate to standardize cancer care nationally.",
    likes: 850,
    commentsList: [
      {
        user_id: 59,
        first_name: "Dr. Pradeep",
        vid_comment: "A must-watch for anyone new to the NCG ecosystem.",
        updated_on: "2023-05-14T12:00:00Z",
      },
      {
        user_id: 60,
        first_name: "Swathi V",
        vid_comment: "200+ centers collaborating is truly remarkable.",
        updated_on: "2023-06-22T09:30:00Z",
      },
      {
        user_id: 61,
        first_name: "Kiran L",
        vid_comment: "This video should be mandatory for all oncology fellows.",
        updated_on: "2023-07-01T15:45:00Z",
      },
      {
        user_id: 62,
        first_name: "Dr. Ghosh",
        vid_comment:
          "The standardisation mission of NCG is exactly what India needs.",
        updated_on: "2023-07-18T10:00:00Z",
      },
      {
        user_id: 63,
        first_name: "Nalini B",
        vid_comment: "Proud to be part of an NCG member institution.",
        updated_on: "2023-08-04T13:15:00Z",
      },
      {
        user_id: 64,
        first_name: "Dr. Trivedi",
        vid_comment:
          "The tumor board standardisation initiative is commendable.",
        updated_on: "2023-08-22T08:30:00Z",
      },
      {
        user_id: 65,
        first_name: "Rohit S",
        vid_comment:
          "NCG's impact on rural cancer care deserves more coverage.",
        updated_on: "2023-09-08T11:00:00Z",
      },
      {
        user_id: 66,
        first_name: "Dr. Mishra",
        vid_comment:
          "Excellent initiative — looking forward to future milestones.",
        updated_on: "2023-09-25T14:30:00Z",
      },
      {
        user_id: 67,
        first_name: "Anusha T",
        vid_comment:
          "The data on treatment standardisation outcomes is very compelling.",
        updated_on: "2023-10-10T09:45:00Z",
      },
      {
        user_id: 68,
        first_name: "Dr. Kulkarni",
        vid_comment: "Best overview of NCG I have come across — well done.",
        updated_on: "2023-10-28T12:00:00Z",
      },
    ],
  },
  {
    id: "10",
    title: "Scalable Access to Evidence-Based Treatment Plans",
    category: "GENERAL",
    subCategory: "Informatics",
    location: "Systemic",
    gender: "All",
    ageGroup: "All Ages",
    date: "Jun 2020",
    hospital: "Tata Memorial Centre",
    isNew: true,
    isPopular: true,
    isSaved: true,
    stageOfCancer: "III",
    image: "https://img.youtube.com/vi/4Y9hPaVcMac/hqdefault.jpg",
    videoUrl: "https://www.youtube.com/watch?v=4Y9hPaVcMac",
    description:
      "Global impact of a clinical informatics system: Scalable delivery of on-time access to evidence-based expert treatment plans across the NCG.",
    likes: 620,
    commentsList: [
      {
        user_id: 69,
        first_name: "Dr. Harini",
        vid_comment:
          "The informatics angle brings a fresh perspective to cancer care.",
        updated_on: "2023-08-10T10:15:00Z",
      },
      {
        user_id: 70,
        first_name: "Suresh M",
        vid_comment: "Impressive scale of implementation across NCG centres.",
        updated_on: "2023-09-03T14:00:00Z",
      },
      {
        user_id: 71,
        first_name: "Dr. Bose",
        vid_comment: "Decision-support at the point of care is game-changing.",
        updated_on: "2023-09-20T09:30:00Z",
      },
      {
        user_id: 72,
        first_name: "Tanya R",
        vid_comment: "The evidence pipeline described here is very robust.",
        updated_on: "2023-10-07T11:45:00Z",
      },
      {
        user_id: 73,
        first_name: "Dr. Varma",
        vid_comment:
          "Would love to see integration with electronic health records.",
        updated_on: "2023-10-24T08:00:00Z",
      },
      {
        user_id: 74,
        first_name: "Prashant G",
        vid_comment:
          "TMC's leadership in clinical informatics is well deserved.",
        updated_on: "2023-11-10T13:15:00Z",
      },
      {
        user_id: 75,
        first_name: "Dr. Aggarwal",
        vid_comment: "The scalability model shown is applicable globally.",
        updated_on: "2023-11-28T10:30:00Z",
      },
      {
        user_id: 76,
        first_name: "Suma K",
        vid_comment: "Excellent use of technology to bridge the knowledge gap.",
        updated_on: "2023-12-15T14:00:00Z",
      },
      {
        user_id: 77,
        first_name: "Dr. Patel",
        vid_comment: "The real-world outcomes data made this very convincing.",
        updated_on: "2024-01-02T09:00:00Z",
      },
      {
        user_id: 78,
        first_name: "Divya N",
        vid_comment:
          "This is exactly the kind of innovation Indian oncology needs.",
        updated_on: "2024-01-19T11:30:00Z",
      },
    ],
  },
];

// --- TAB UTILITY ---
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}
// Uses CSS display:none instead of unmounting — keeps YouTube player DOM node alive across views
function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      style={{ display: value !== index ? "none" : "block" }}
      {...other}
    >
      <Box sx={index === 0 ? { pt: 3 } : { pt: 0 }}>{children}</Box>
    </div>
  );
}

// --- FILTER MODAL ---
interface NCGFilterModalProps {
  open: boolean;
  title: string;
  options: { val: string; count: number }[];
  selected: string[];
  onApply: (values: string[]) => void;
  onClose: () => void;
}

function NCGFilterModal({
  open,
  title,
  options,
  selected,
  onApply,
  onClose,
}: NCGFilterModalProps) {
  const [local, setLocal] = useState<string[]>(selected);

  // Sync local state whenever the modal opens
  useEffect(() => {
    if (open) setLocal(selected);
  }, [open]);

  const toggle = (val: string) =>
    setLocal((prev) =>
      prev.includes(val) ? prev.filter((v) => v !== val) : [...prev, val],
    );

  return (
    <Dialog open={open} onClose={onClose} maxWidth={false}>
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pr: 1,
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          {title}
        </Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ width: { xs: "100%", sm: 400 }, minWidth: 280 }}>
        {options.map((opt) => (
          <Stack
            key={opt.val}
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack direction="row" alignItems="center" minWidth={180}>
              <Checkbox
                size="small"
                checked={local.includes(opt.val)}
                onClick={() => toggle(opt.val)}
              />
              <Typography variant="body2">{opt.val}</Typography>
            </Stack>
            {/* <Typography variant="body2" color="text.secondary">
              ({opt.count})
            </Typography> */}
          </Stack>
        ))}

        <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
          <Button
            variant="contained"
            onClick={() => {
              onApply(local);
              onClose();
            }}
          >
            Apply
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              setLocal([]);
              onApply([]);
              onClose();
            }}
          >
            Clear
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

// --- MAIN COMPONENT ---
export default function NCGModule() {
  // Navigation State: 0 = Library Grid, 1 = Video Player
  const [currentView, setCurrentView] = useState(0);

  const [videos, setVideos] = useState<VideoRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Filter & search state
  const [searchQuery, setSearchQuery] = useState("");
  const [sortOrder, setSortOrder] = useState("sort");
  const [filterDmg, setFilterDmg] = useState<string[]>([]);
  const [filterCategory, setFilterCategory] = useState<string[]>([]);
  const [filterAgeGroup, setFilterAgeGroup] = useState<string[]>([]);
  const [filterGender, setFilterGender] = useState<string[]>([]);
  const [filterSite, setFilterSite] = useState<string[]>([]);
  const [filterStage, setFilterStage] = useState<string[]>([]);
  const [filterStatus, setFilterStatus] = useState("");

  // Filter modal open states
  const [dmgModalOpen, setDmgModalOpen] = useState(false);
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [ageGroupModalOpen, setAgeGroupModalOpen] = useState(false);
  const [genderModalOpen, setGenderModalOpen] = useState(false);
  const [siteModalOpen, setSiteModalOpen] = useState(false);
  const [stageModalOpen, setStageModalOpen] = useState(false);

  const [selectedVideo, setSelectedVideo] = useState<VideoRecord | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Tracking & Player State
  const [watchProgress, setWatchProgress] = useState<number>(0);
  const [isCompleted, setIsCompleted] = useState<boolean>(false);
  const [playerError, setPlayerError] = useState<boolean>(false);
  const playerRef = useRef<any>(null);
  const playerReadyRef = useRef<boolean>(false);
  const selectedVideoRef = useRef<VideoRecord | null>(null);
  const prevViewValue = useRef<number>(0);
  const progressInterval = useRef<NodeJS.Timeout | null>(null);

  // --- LIKE & COMMENT STATE ---
  const [likeCount, setLikeCount] = useState<number>(0);
  const [isLiked, setIsLiked] = useState<boolean>(false);
  const [comments, setComments] = useState<Comment[]>([]);

  // --- DERIVED: parse "Mon YYYY" date strings for sorting ---
  const parseDate = (d: string) => {
    const [month, year] = d.split(" ");
    return new Date(`${month} 1, ${year}`);
  };

  // --- DERIVED: filtered + sorted video list ---
  const filteredVideos = useMemo(() => {
    let result = [...videos];

    // Apply popup filters (multi-select — OR within a filter, AND across filters)
    if (filterDmg.length)
      result = result.filter((v) => filterDmg.includes(v.subCategory));
    if (filterCategory.length)
      result = result.filter((v) => filterCategory.includes(v.category));
    if (filterAgeGroup.length)
      result = result.filter((v) => filterAgeGroup.includes(v.ageGroup));
    if (filterGender.length)
      result = result.filter((v) => filterGender.includes(v.gender));
    if (filterSite.length)
      result = result.filter((v) => filterSite.includes(v.location));
    if (filterStage.length)
      result = result.filter((v) => filterStage.includes(v.stageOfCancer));

    // Independent status filter (single selection)
    if (filterStatus === "Saved") result = result.filter((v) => v.isSaved);
    else if (filterStatus === "Popular")
      result = result.filter((v) => v.isPopular);
    else if (filterStatus === "New") result = result.filter((v) => v.isNew);

    // Apply search query
    if (searchQuery)
      result = result.filter(
        (v) =>
          v.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          v.hospital.toLowerCase().includes(searchQuery.toLowerCase()),
      );

    // Apply sorting
    if (sortOrder === "latest")
      result.sort(
        (a, b) => parseDate(b.date).getTime() - parseDate(a.date).getTime(),
      );
    else if (sortOrder === "earliest")
      result.sort(
        (a, b) => parseDate(a.date).getTime() - parseDate(b.date).getTime(),
      );

    return result;
  }, [
    videos,
    filterDmg,
    filterCategory,
    filterAgeGroup,
    filterGender,
    filterSite,
    filterStage,
    searchQuery,
    sortOrder,
    filterStatus,
  ]);

  // --- API FETCH SIMULATION ---
  useEffect(() => {
    const fetchVideoLibrary = async () => {
      setLoading(true);
      setTimeout(() => {
        setVideos(dummyJsonData);
        setSelectedVideo(dummyJsonData[0]);
        setLoading(false);
      }, 500);
    };
    fetchVideoLibrary();
  }, []);

  // Keep selectedVideoRef in sync
  useEffect(() => {
    selectedVideoRef.current = selectedVideo;
  }, [selectedVideo]);

  // --- RESET TRACKING ON VIDEO CHANGE ---
  useEffect(() => {
    setWatchProgress(0);
    setIsCompleted(false);
  }, [selectedVideo]);

  // --- NATIVE YOUTUBE PLAYER INTEGRATION ---
  useEffect(() => {
    // Only build player when currentView is 1 (Player)
    if (currentView !== 1 || !selectedVideo) return;

    const videoId = extractVideoId(selectedVideo.videoUrl);
    if (!videoId) return;

    const viewJustSwitched = prevViewValue.current !== 1;
    prevViewValue.current = currentView;

    const createPlayer = () => {
      if (playerRef.current && playerReadyRef.current) {
        if (viewJustSwitched) {
          playerRef.current.loadVideoById(videoId);
        }
        return;
      }

      if (playerRef.current && !playerReadyRef.current) return;

      const container = document.getElementById("youtube-player-container");
      if (!container) return;

      playerRef.current = new window.YT.Player("youtube-player-container", {
        videoId: videoId,
        playerVars: { autoplay: 1, mute: 0, rel: 0 },
        events: {
          onReady: () => {
            playerReadyRef.current = true;
            const latestId = selectedVideoRef.current
              ? extractVideoId(selectedVideoRef.current.videoUrl)
              : videoId;
            if (latestId && playerRef.current) {
              playerRef.current.loadVideoById(latestId);
            }
          },
          onError: () => {
            setPlayerError(true);
          },
          onStateChange: (event: any) => {
            if (event.data === window.YT.PlayerState.ENDED) {
              setIsCompleted(true);
              setWatchProgress(100);
              if (progressInterval.current)
                clearInterval(progressInterval.current);
            }
            if (event.data === window.YT.PlayerState.PLAYING) {
              progressInterval.current = setInterval(() => {
                if (playerRef.current && playerRef.current.getCurrentTime) {
                  const current = playerRef.current.getCurrentTime();
                  const total = playerRef.current.getDuration();
                  if (total > 0)
                    setWatchProgress(Math.round((current / total) * 100));
                }
              }, 1000);
            } else {
              if (progressInterval.current)
                clearInterval(progressInterval.current);
            }
          },
        },
      });
    };

    if (!window.YT) {
      const tag = document.createElement("script");
      tag.src = "https://www.youtube.com/iframe_api";
      const firstScriptTag = document.getElementsByTagName("script")[0];
      firstScriptTag?.parentNode?.insertBefore(tag, firstScriptTag);
      window.onYouTubeIframeAPIReady = () => createPlayer();
    } else {
      createPlayer();
    }

    return () => {
      if (progressInterval.current) clearInterval(progressInterval.current);
    };
  }, [selectedVideo, currentView]);

  // Seed like count and comments from dummy data when video changes
  useEffect(() => {
    if (!selectedVideo) return;
    setLikeCount(selectedVideo.likes);
    setIsLiked(false);
    setComments(selectedVideo.commentsList ?? []);
    setPlayerError(false);
  }, [selectedVideo?.id]);

  // --- HANDLERS ---
  const handleCardClick = (video: VideoRecord) => {
    setSelectedVideo(video);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleBookmarkToggle = (e: React.MouseEvent, videoId: string) => {
    e.stopPropagation();
    setVideos((prev) =>
      prev.map((v) => (v.id === videoId ? { ...v, isSaved: !v.isSaved } : v)),
    );
  };

  const handleWatchVideo = () => {
    setIsModalOpen(false);
    setCurrentView(1); // Navigate to Video Player
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSidebarClick = (video: VideoRecord) => {
    const videoId = extractVideoId(video.videoUrl);
    selectedVideoRef.current = video;
    setSelectedVideo(video);
    if (videoId && playerRef.current && playerReadyRef.current) {
      playerRef.current.loadVideoById(videoId);
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleLikeToggle = (nextLiked: boolean) => {
    setIsLiked(nextLiked);
    setLikeCount((prev) => (nextLiked ? prev + 1 : Math.max(0, prev - 1)));
    if (selectedVideo) {
      setVideos((prev) =>
        prev.map((v) =>
          v.id === selectedVideo.id
            ? {
                ...v,
                likes: nextLiked ? v.likes + 1 : Math.max(0, v.likes - 1),
              }
            : v,
        ),
      );
    }
  };

  const handleAddComment = (newComment: Comment) => {
    setComments((prev) => [newComment, ...prev]);
    if (selectedVideo) {
      setVideos((prev) =>
        prev.map((v) =>
          v.id === selectedVideo.id
            ? { ...v, commentsList: [newComment, ...v.commentsList] }
            : v,
        ),
      );
    }
  };

  // --------------------------------------------------------
  // VIEW 0: Library Grid & Filters
  // --------------------------------------------------------
  const LibraryView = () => {
    if (loading)
      return (
        <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
          <CircularProgress />
        </Box>
      );

    if (error)
      return (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      );

    return (
      <Grid container spacing={4}>
        <Grid item xs={12} md={3}>
          <Box
            sx={{
              backgroundColor: "white",
              border: "1px solid #e0e0e0",
              borderRadius: 2,
              mt: 1,
              px: "7%",
              py: "5%",
              boxShadow: 1,
            }}
          >
            {/* Header: Filters + Clear All */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
              }}
            >
              <Typography sx={{ fontWeight: 700, fontSize: "1.125rem" }}>
                Filters
              </Typography>
              <Button
                size="small"
                onClick={() => {
                  setFilterDmg([]);
                  setFilterCategory([]);
                  setFilterAgeGroup([]);
                  setFilterGender([]);
                  setFilterSite([]);
                  setFilterStage([]);
                }}
              >
                Clear All
              </Button>
            </Box>
            <Divider />

            {/* DMG */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
                mt: "2%",
                mb: "2%",
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.875rem",
                  alignSelf: "center",
                  fontWeight: 600,
                }}
              >
                DMGs
              </Typography>
              <Button
                color="primary"
                onClick={() => setDmgModalOpen(true)}
                sx={{ minWidth: 0 }}
              >
                <ControlPointIcon />
              </Button>
            </Box>
            <Divider />

            {/* Category */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
                mt: "2%",
                mb: "2%",
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.875rem",
                  alignSelf: "center",
                  fontWeight: 600,
                }}
              >
                Category
              </Typography>
              <Button
                color="primary"
                onClick={() => setCategoryModalOpen(true)}
                sx={{ minWidth: 0 }}
              >
                <ControlPointIcon />
              </Button>
            </Box>
            <Divider />

            {/* Age Group */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
                mt: "2%",
                mb: "2%",
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.875rem",
                  alignSelf: "center",
                  fontWeight: 600,
                }}
              >
                Age Group
              </Typography>
              <Button
                color="primary"
                onClick={() => setAgeGroupModalOpen(true)}
                sx={{ minWidth: 0 }}
              >
                <ControlPointIcon />
              </Button>
            </Box>
            <Divider />

            {/* Gender */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
                mt: "2%",
                mb: "2%",
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.875rem",
                  alignSelf: "center",
                  fontWeight: 600,
                }}
              >
                Gender
              </Typography>
              <Button
                color="primary"
                onClick={() => setGenderModalOpen(true)}
                sx={{ minWidth: 0 }}
              >
                <ControlPointIcon />
              </Button>
            </Box>
            <Divider />

            {/* Site */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
                mt: "2%",
                mb: "2%",
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.875rem",
                  alignSelf: "center",
                  fontWeight: 600,
                }}
              >
                Site
              </Typography>
              <Button
                color="primary"
                onClick={() => setSiteModalOpen(true)}
                sx={{ minWidth: 0 }}
              >
                <ControlPointIcon />
              </Button>
            </Box>
            <Divider />

            {/* Stage of Cancer */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
                mt: "2%",
                mb: "2%",
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.875rem",
                  alignSelf: "center",
                  fontWeight: 600,
                }}
              >
                Stage of Cancer
              </Typography>
              <Button
                color="primary"
                onClick={() => setStageModalOpen(true)}
                sx={{ minWidth: 0 }}
              >
                <ControlPointIcon />
              </Button>
            </Box>
            <Divider />
          </Box>

          {/* Independent Filters card */}
          <Box
            sx={{
              backgroundColor: "white",
              border: "1px solid #e0e0e0",
              borderRadius: 2,
              mt: 2,
              px: "7%",
              py: "5%",
              boxShadow: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
              }}
            >
              <Typography sx={{ fontWeight: 700, fontSize: "1.125rem" }}>
                Independent Filters
              </Typography>
              <Button
                size="small"
                onClick={() => setFilterStatus("")}
                disabled={!filterStatus}
              >
                Clear
              </Button>
            </Box>
            <Divider sx={{ mb: 1 }} />
            <FormControl component="fieldset">
              <RadioGroup value={filterStatus}>
                {["Saved", "Popular", "New"].map((option) => (
                  <FormControlLabel
                    key={option}
                    value={option}
                    control={
                      <Radio
                        size="small"
                        onClick={() =>
                          setFilterStatus((prev) =>
                            prev === option ? "" : option,
                          )
                        }
                      />
                    }
                    label={<Typography variant="body2">{option}</Typography>}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </Box>
        </Grid>

        <Grid item xs={12} md={9}>
          <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by title or hospital"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ bgcolor: "white" }}
              InputProps={{ endAdornment: <SearchIcon color="action" /> }}
            />
            <TextField
              select
              size="small"
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              sx={{ width: 200, bgcolor: "white" }}
            >
              <MenuItem value="sort">SORT</MenuItem>
              <MenuItem value="latest">Presenting Year (Latest First)</MenuItem>
              <MenuItem value="earliest">
                Presenting Year (Earliest First)
              </MenuItem>
            </TextField>
          </Box>

          <Grid container spacing={3}>
            {filteredVideos.map((video) => (
              <Grid item xs={12} sm={6} md={4} key={video.id}>
                <Card
                  sx={{
                    cursor: "pointer",
                    "&:hover": { boxShadow: 6 },
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                  }}
                  onClick={() => handleCardClick(video)}
                >
                  <Box
                    sx={{
                      bgcolor: "#0f4c81",
                      color: "white",
                      px: 2,
                      py: 0.5,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: "bold" }}>
                      {video.category}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                      {video.isNew && (
                        <Chip
                          label="NEW"
                          size="small"
                          sx={{
                            bgcolor: "#d7c062",
                            color: "white",
                            height: 20,
                            fontSize: "0.65rem",
                            fontWeight: "bold",
                          }}
                        />
                      )}
                      {video.isPopular && (
                        <Chip
                          label="POPULAR"
                          size="small"
                          sx={{
                            bgcolor: "#00b0f0",
                            color: "white",
                            height: 20,
                            fontSize: "0.65rem",
                            fontWeight: "bold",
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                  <Box sx={{ position: "relative" }}>
                    <CardMedia
                      component="img"
                      height="140"
                      image={video.image}
                      alt={video.title}
                    />
                    {/* <Box
                      sx={{
                        position: "absolute",
                        top: 8,
                        left: 8,
                        display: "flex",
                        gap: 0.5,
                      }}
                    >
                      {video.isNew && (
                        <Chip
                          label="NEW"
                          size="small"
                          sx={{
                            bgcolor: "#ff3b30",
                            color: "white",
                            height: 20,
                            fontSize: "0.65rem",
                            fontWeight: "bold",
                          }}
                        />
                      )}
                      {video.isPopular && (
                        <Chip
                          label="POPULAR"
                          size="small"
                          sx={{
                            bgcolor: "#00b0f0",
                            color: "white",
                            height: 20,
                            fontSize: "0.65rem",
                            fontWeight: "bold",
                          }}
                        />
                      )}
                    </Box> */}
                    <Box
                      sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                      }}
                    >
                      <PlayCircleOutlineIcon
                        sx={{
                          color: "white",
                          fontSize: 48,
                          opacity: 0.9,
                          filter: "drop-shadow(0px 0px 4px rgba(0,0,0,0.5))",
                        }}
                      />
                    </Box>
                  </Box>
                  <CardContent sx={{ flexGrow: 1, p: 2 }}>
                    <Typography
                      variant="subtitle2"
                      color="primary"
                      sx={{ fontWeight: "bold", mb: 1, lineHeight: 1.2 }}
                    >
                      {video.title}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ mb: 1 }}
                    >
                      {video.subCategory}
                    </Typography>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <LocationOnIcon sx={{ fontSize: 14, color: "gray" }} />
                      <Typography variant="caption" color="text.secondary">
                        {video.hospital}
                      </Typography>
                    </Box>
                  </CardContent>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      px: 2,
                      pb: 1.5,
                      pt: 0,
                    }}
                  >
                    {/* Likes & Comments */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          color: "text.secondary",
                        }}
                      >
                        <ThumbUpOutlinedIcon sx={{ fontSize: 18 }} />
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                          {video.likes}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          color: "text.secondary",
                        }}
                      >
                        <ChatBubbleOutlineIcon sx={{ fontSize: 18 }} />
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                          {video.commentsList.length}
                        </Typography>
                      </Box>

                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 600, color: "text.secondary" }}
                      >
                        {`Stage-${video.stageOfCancer}`}
                      </Typography>
                    </Box>

                    {/* Bookmark Toggle */}
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={(e) => handleBookmarkToggle(e, video.id)}
                    >
                      {video.isSaved ? (
                        <BookmarkIcon />
                      ) : (
                        <BookmarkBorderIcon />
                      )}
                    </IconButton>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Grid>

        <Dialog
          open={isModalOpen}
          onClose={handleCloseModal}
          maxWidth="sm"
          fullWidth
        >
          {selectedVideo && (
            <>
              <DialogTitle
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  pb: 1,
                }}
              >
                <Typography
                  variant="h6"
                  color="primary"
                  sx={{ fontWeight: "bold", pr: 4 }}
                >
                  {selectedVideo.title}
                </Typography>
                <IconButton
                  onClick={handleCloseModal}
                  size="small"
                  sx={{ position: "absolute", right: 8, top: 8 }}
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent>
                <img
                  src={selectedVideo.image}
                  alt={selectedVideo.title}
                  style={{ width: "100%", borderRadius: 8, marginBottom: 16 }}
                />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  {selectedVideo.description}
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<PlayCircleOutlineIcon />}
                  sx={{
                    // bgcolor: "#ff4d4f",
                    // "&:hover": { bgcolor: "#ff7875" },

                    bgcolor: "#8870D7",
                    // "&:hover": { bgcolor: "#ff7875" },
                    borderRadius: 2,
                    textTransform: "none",
                    px: 3,
                    width: "100%",
                  }}
                  onClick={handleWatchVideo}
                >
                  Watch Video Now
                </Button>
              </DialogContent>
            </>
          )}
        </Dialog>

        {/* Filter Modals */}
        <NCGFilterModal
          open={dmgModalOpen}
          title="Filter DMGs"
          options={[...new Set(videos.map((v) => v.subCategory))].map(
            (val) => ({
              val,
              count: videos.filter((v) => v.subCategory === val).length,
            }),
          )}
          selected={filterDmg}
          onApply={setFilterDmg}
          onClose={() => setDmgModalOpen(false)}
        />
        <NCGFilterModal
          open={categoryModalOpen}
          title="Filter Category"
          options={[...new Set(videos.map((v) => v.category))].map((val) => ({
            val,
            count: videos.filter((v) => v.category === val).length,
          }))}
          selected={filterCategory}
          onApply={setFilterCategory}
          onClose={() => setCategoryModalOpen(false)}
        />
        <NCGFilterModal
          open={ageGroupModalOpen}
          title="Filter Age Group"
          options={[...new Set(videos.map((v) => v.ageGroup))].map((val) => ({
            val,
            count: videos.filter((v) => v.ageGroup === val).length,
          }))}
          selected={filterAgeGroup}
          onApply={setFilterAgeGroup}
          onClose={() => setAgeGroupModalOpen(false)}
        />
        <NCGFilterModal
          open={genderModalOpen}
          title="Filter Gender"
          options={[...new Set(videos.map((v) => v.gender))].map((val) => ({
            val,
            count: videos.filter((v) => v.gender === val).length,
          }))}
          selected={filterGender}
          onApply={setFilterGender}
          onClose={() => setGenderModalOpen(false)}
        />
        <NCGFilterModal
          open={siteModalOpen}
          title="Filter Site"
          options={[...new Set(videos.map((v) => v.location))].map((val) => ({
            val,
            count: videos.filter((v) => v.location === val).length,
          }))}
          selected={filterSite}
          onApply={setFilterSite}
          onClose={() => setSiteModalOpen(false)}
        />
        <NCGFilterModal
          open={stageModalOpen}
          title="Filter Stage of Cancer"
          options={["I", "II", "III", "IV"].map((val) => ({
            val,
            count: videos.filter((v) => v.stageOfCancer === val).length,
          }))}
          selected={filterStage}
          onApply={setFilterStage}
          onClose={() => setStageModalOpen(false)}
        />
      </Grid>
    );
  };

  // --------------------------------------------------------
  // VIEW 1: Video Player & Sidebar
  // --------------------------------------------------------
  const DetailView = () => {
    if (loading)
      return (
        <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
          <CircularProgress />
        </Box>
      );
    if (!selectedVideo) return null;

    return (
      <Box>
        {/* BACK NAVIGATION BUTTON */}
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => {
            setCurrentView(0);
            if (playerRef.current && playerRef.current.pauseVideo) {
              playerRef.current.pauseVideo();
            }
          }}
          sx={{ mb: 2, textTransform: "none", fontWeight: "bold" }}
        >
          Back to Video Library
        </Button>

        <Box sx={{ bgcolor: "white", p: 3, borderRadius: 2, boxShadow: 1 }}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={8}>
              <Box
                sx={{
                  width: "100%",
                  paddingTop: "56.25%",
                  position: "relative",
                  bgcolor: "black",
                  borderRadius: 2,
                  overflow: "hidden",
                }}
              >
                <div
                  id="youtube-player-container"
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    display: playerError ? "none" : "block",
                  }}
                ></div>
                {playerError && (
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1.5,
                      bgcolor: "#111",
                      px: 3,
                      textAlign: "center",
                    }}
                  >
                    <Typography variant="body1" sx={{ color: "#fff" }}>
                      This video cannot be played here.
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#aaa" }}>
                      Watch it directly on YouTube:
                    </Typography>
                    <Button
                      variant="contained"
                      size="small"
                      href={selectedVideo.videoUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ textTransform: "none" }}
                    >
                      Open on YouTube
                    </Button>
                  </Box>
                )}
              </Box>

              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  bgcolor: "#f8f9fa",
                  border: "1px solid #e0e0e0",
                  borderRadius: 2,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                    alignItems: "center",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: "bold",
                      color: isCompleted ? "success.main" : "text.secondary",
                    }}
                  >
                    {isCompleted
                      ? "Video Completed"
                      : `Video Progress: ${watchProgress}%`}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={watchProgress}
                  color={isCompleted ? "success" : "primary"}
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>

              <Typography
                variant="h5"
                color="primary"
                sx={{ fontWeight: "bold", mt: 3, mb: 1 }}
              >
                {selectedVideo.title}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                {selectedVideo.description}
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 3,
                  color: "text.secondary",
                  flexWrap: "wrap",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <PersonIcon fontSize="small" />
                  <Typography variant="body2">
                    {selectedVideo.ageGroup}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CalendarTodayIcon fontSize="small" />
                  <Typography variant="body2">{selectedVideo.date}</Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <LocationOnIcon fontSize="small" />
                  <Typography variant="body2">
                    {selectedVideo.hospital}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="body2">{`Stage of Cancer: ${selectedVideo.stageOfCancer}`}</Typography>
                </Box>
                <LikeButton
                  likes={likeCount}
                  isInitiallyLiked={isLiked}
                  onToggle={handleLikeToggle}
                />
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Typography
                variant="h6"
                color="primary"
                sx={{
                  fontWeight: "bold",
                  mb: 2,
                  borderBottom: "2px solid #0f4c81",
                  display: "inline-block",
                  pb: 0.5,
                }}
              >
                Up Next in Library
              </Typography>

              <List
                sx={{
                  width: "100%",
                  bgcolor: "background.paper",
                  maxHeight: "300px",
                  overflow: "auto",
                  "&::-webkit-scrollbar": { width: "6px" },
                  "&::-webkit-scrollbar-track": {
                    backgroundColor: "#f0f0f0",
                    borderRadius: "4px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#888",
                    borderRadius: "4px",
                  },
                  "&::-webkit-scrollbar-thumb:hover": {
                    backgroundColor: "#555",
                  },
                }}
              >
                {videos.map((vid) => (
                  <ListItem key={vid.id} disablePadding sx={{ mb: 1 }}>
                    <ListItemButton
                      alignItems="flex-start"
                      onClick={() => handleSidebarClick(vid)}
                      sx={{
                        borderRadius: 1,
                        bgcolor:
                          selectedVideo.id === vid.id
                            ? "#f0f7ff"
                            : "transparent",
                        border:
                          selectedVideo.id === vid.id
                            ? "1px solid #cce5ff"
                            : "1px solid transparent",
                      }}
                    >
                      <ListItemAvatar sx={{ mr: 2 }}>
                        <Avatar
                          variant="rounded"
                          src={vid.image}
                          sx={{ width: 100, height: 56 }}
                        />
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: "bold",
                              lineHeight: 1.2,
                              color:
                                selectedVideo.id === vid.id
                                  ? "primary.main"
                                  : "text.primary",
                            }}
                          >
                            {vid.title}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            {vid.hospital}
                          </Typography>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>

              <Box mt={3}>
                <CommentsSection
                  comments={comments}
                  onAddComment={handleAddComment}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ bgcolor: "#f5f5f5", minHeight: "100vh", pb: 5 }}>
      <Box sx={{ bgcolor: "white", borderBottom: "1px solid #ddd", mb: 3 }}>
        <Container maxWidth="xl">
          <Box sx={{ display: "flex", alignItems: "center", py: 2, gap: 2 }}>
            {NCG_logo && (
              <img src={NCG_logo} alt="logo" style={{ height: "40px" }} />
            )}
            <Typography
              variant="h6"
              color="primary"
              sx={{ fontWeight: "bold", letterSpacing: 1 }}
            >
              NCG VIRTUAL TUMOUR BOARD VIDEO LIBRARY
            </Typography>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="xl">
        {/* Render Library Grid */}
        {currentView === 0 && (
          <Box>
            <Typography sx={{ mb: 2, color: "text.secondary" }}>
              Welcome to the NCG Virtual Tumor Board Video Library, your
              comprehensive resource for oncology professionals to enhance
              research and collaboration in tumor diagnosis and treatment.
            </Typography>
          </Box>
        )}
        <CustomTabPanel value={currentView} index={0}>
          {LibraryView()}
        </CustomTabPanel>

        {/* Render Video Player */}
        <CustomTabPanel value={currentView} index={1}>
          {DetailView()}
        </CustomTabPanel>
      </Container>
    </Box>
  );
}
